import { ReactNode } from "react";

import { Sidebar } from "@/components/Layout/BaseLayouts/Sidebar/Sidebar";
import { BottomNavBar } from "@/components/Layout/BottomNavBar/BottomNavBar";
import { useIsPortalChromeHidden } from "@/hooks/useBodyScrollLock";
import { useIsBelowDesktopBreakpoint } from "@/hooks/useIsBelowDesktopBreakpoint";
import { ScrollDirectionProvider } from "@/provider/ScrollDirectionProvider/ScrollDirectionProvider";
import { FCC } from "@/types";

import { ContentContainer } from "./ContentContainer";
import { Header } from "./Header";
import { SkipToContent } from "./SkipToContent";

interface Props {
  /**
   * Full-width banners (e.g. AgentLoginBanner, PortalTopBanners) rendered
   * below the header and above the content column, next to the sidebar.
   */
  banners?: ReactNode;
}

/**
 * Every direct child passed to PortalPageLayout must declare its own col-span
 * - there's no full-width fallback.
 */
export const PortalPageLayout: FCC<Props> = ({ children, banners }) => {
  const isBelowDesktop = useIsBelowDesktopBreakpoint();
  const hidePortalChrome = useIsPortalChromeHidden();

  return (
    <div className="flex min-h-screen flex-col bg-background-primary">
      {!hidePortalChrome && <SkipToContent />}
      {isBelowDesktop ? (
        <ScrollDirectionProvider>
          <div className="flex flex-grow flex-col">
            {!hidePortalChrome && <Header variant="portal" isMobile={true} />}
            {!hidePortalChrome && banners}
            {/* Both the mobile header and BottomNavBar are `position: fixed`
                (removed from flow), so this isn't decorative spacing - it's
                compensating for:
                - top: 56px header + 16px gap = 72px
                - bottom: 72px nav + 16px gap = 88px (see REDESIGN.md §2.5)
                Dropped during pinned mobile edit — the sticky card owns the
                viewport and chrome is hidden so users cannot navigate away. */}
            <ContentContainer
              className={hidePortalChrome ? undefined : "!pb-[88px] !pt-[72px]"}
            >
              {children}
            </ContentContainer>
            {!hidePortalChrome && <BottomNavBar />}
          </div>
        </ScrollDirectionProvider>
      ) : (
        <div className="flex flex-grow">
          <Sidebar />
          <div className="flex flex-grow flex-col">
            {banners}
            <ContentContainer>{children}</ContentContainer>
          </div>
        </div>
      )}
    </div>
  );
};
